import { useMutation, useQuery } from '@tanstack/react-query';
import { FormikErrors, FormikValues } from 'formik';
import { api } from '@/modules/common/api/index.ts';
import { queryClient } from '@/modules/common/query.ts';
import { withErrorHandling } from '@/modules/common/utils/error-utils.tsx';
import {
  CURRENT_ENTITY_QUERY_KEY,
  CURRENT_REGISTER_APPLY_ID_QUERY_KEY,
  CURRENT_REGISTER_CLASS_APPLY_ID_QUERY_KEY,
  USER_ENTITIES_QUERY_KEY,
} from '@/modules/entity/queries/entity.tsx';
import { CURRENT_USER_ACTIVITIES_QUERY_KEY } from '@/modules/investor-dashboard/queries/activity.tsx';
import { INVESTOR_CERTIFICATES_LIST_QUERY_KEY } from '@/modules/investor-dashboard/queries/certificates.ts';
import { CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY } from '@/modules/investor-dashboard/queries/shareholders.tsx';
import {
  CURRENT_ISSUER_ACTIVITIES_QUERY_KEY,
  CURRENT_ISSUER_TRANSACTIONS_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/activities.tsx';
import { ISSUER_CERTIFICATES_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/certificates.ts';
import {
  ISSUED_CAPITAL_KEY,
  ISSUER_ISSUED_CAPITAL_OVER_TIME_CHART_KEY,
  ISSUER_PENDING_APPLICATION_SUMMARY_CHART_KEY,
  ISSUER_PENDING_APPLICATION_SUMMARY_SECTION_KEY,
} from '@/modules/issuer-dashboard/queries/charts.ts';
import {
  APPLICATIONS_OVER_TIME_SUMMARY_QUERY_KEY,
  ISSUED_CAPITAL_SUMMARY_KEY,
  PENDING_APPLICATIONS_KEY,
  PENDING_APPLICATIONS_SUMMARY_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/dashboard.ts';
import { ISSUER_DOCUMENTS_QUERY_KEY } from '@/modules/issuer-dashboard/queries/documents.ts';
import { ISSUER_REGISTER_NAV_QUERY_KEY } from '@/modules/issuer-dashboard/queries/nav.tsx';
import { ISSUER_REGISTER_CLASS_LIST_QUERY_KEY } from '@/modules/issuer-dashboard/queries/registers.tsx';
import {
  CHARTS_ISSUER_ISSUED_CAPITAL_OVERTIME_QUERY_KEY,
  ISSUER_REGISTER_CLASSES_UNITS_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/securities.ts';
import {
  SHAREHOLDERS_HOLDINGS_QUERY_KEY,
  SHAREHOLDER_BALANCE_QUERY_KEY,
  SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY,
  TOP_HOLDERS_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/shareholders.tsx';
import { CURRENT_ISSUER_TRANSACTION_BY_ID_QUERY_KEY } from '@/modules/issuer-dashboard/queries/transactions.ts';
import {
  CURRENT_ISSUER_TRANSFERS_QUERY_KEY,
  CURRENT_ISSUER_TRANSFER_BY_ID_QUERY_KEY,
} from '@/modules/issuer-dashboard/queries/transfers.tsx';
import {
  CreateTransactionRequest,
  GetEntityShareholdersResponse,
  TransactionSetAwaitPaymentStatusRequest,
  TransactionSetRejectStatusRequest,
} from 'api';

export const CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY = 'vestra/entity-shareholders';

export const useCreateTransactionMutation = ({
  setErrors,
  isSubscriptionAfterEntityCreate = false,
  onSuccess,
}: {
  setErrors: (errors: FormikErrors<FormikValues>) => void;
  isSubscriptionAfterEntityCreate?: boolean;
  onSuccess?: () => void;
}) => {
  return useMutation({
    mutationFn: async (payload: CreateTransactionRequest & { register_id?: number }) => {
      const { register_id, ...data } = payload;
      const response = await withErrorHandling(
        api.v1.createTransactionV1SrTransactionsPost(
          data,
          register_id ? { headers: { ['xRegister-ID']: register_id } } : undefined,
        ),
        {
          notificationDuration: isSubscriptionAfterEntityCreate ? 20000 : undefined,
          loading: 'Creating transaction...',
          success: isSubscriptionAfterEntityCreate
            ? "You have succsefully applied for unit/shares. Please wait for the issuer to process your transaction. After it's approved by the issuer, you will receive payment instructions via email."
            : 'Transaction created',
          setErrors,
        },
      );
      return response.data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSACTIONS_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_DOCUMENTS_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_CERTIFICATES_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([INVESTOR_CERTIFICATES_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSFERS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ISSUER_ACTIVITIES_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_REGISTER_NAV_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUER_REGISTER_CLASS_LIST_QUERY_KEY]);
      await queryClient.invalidateQueries([SHAREHOLDERS_HOLDINGS_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSACTION_BY_ID_QUERY_KEY]);
      await queryClient.invalidateQueries([CURRENT_ISSUER_TRANSFER_BY_ID_QUERY_KEY]);
      //
      await queryClient.invalidateQueries([ISSUER_PENDING_APPLICATION_SUMMARY_CHART_KEY]);
      await queryClient.invalidateQueries([ISSUER_PENDING_APPLICATION_SUMMARY_SECTION_KEY]);
      await queryClient.invalidateQueries([ISSUED_CAPITAL_SUMMARY_KEY]);
      await queryClient.invalidateQueries([ISSUER_ISSUED_CAPITAL_OVER_TIME_CHART_KEY]);
      await queryClient.invalidateQueries({
        queryKey: [CHARTS_ISSUER_ISSUED_CAPITAL_OVERTIME_QUERY_KEY],
      });
      await queryClient.invalidateQueries({ queryKey: [ISSUER_REGISTER_CLASSES_UNITS_QUERY_KEY] });
      await queryClient.invalidateQueries([PENDING_APPLICATIONS_KEY]);
      await queryClient.invalidateQueries([APPLICATIONS_OVER_TIME_SUMMARY_QUERY_KEY]);
      await queryClient.invalidateQueries([PENDING_APPLICATIONS_SUMMARY_QUERY_KEY]);
      await queryClient.invalidateQueries([ISSUED_CAPITAL_KEY]);
      await queryClient.invalidateQueries([TOP_HOLDERS_QUERY_KEY]);
      await queryClient.invalidateQueries([SHAREHOLDER_BALANCE_STATISTIC_QUERY_KEY]);
      await queryClient.invalidateQueries([SHAREHOLDER_BALANCE_QUERY_KEY]);

      if (isSubscriptionAfterEntityCreate) {
        queryClient.invalidateQueries([CURRENT_ENTITY_QUERY_KEY]);
        queryClient.invalidateQueries([USER_ENTITIES_QUERY_KEY]);
        queryClient.invalidateQueries([CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY]);
        queryClient.invalidateQueries([CURRENT_REGISTER_SHAREHOLDERS_QUERY_KEY]);
        queryClient.invalidateQueries([CURRENT_USER_ACTIVITIES_QUERY_KEY]);

        queryClient.removeQueries([CURRENT_REGISTER_APPLY_ID_QUERY_KEY]);
        queryClient.removeQueries([CURRENT_REGISTER_CLASS_APPLY_ID_QUERY_KEY]);
      }
      onSuccess && onSuccess();
    },
  });
};

export const useCurrentEntityShareholdersQuery = (entity_id: number, disabled = false) => {
  return useQuery<GetEntityShareholdersResponse>({
    queryKey: [CURRENT_ENTITY_SHAREHOLDERS_QUERY_KEY, +entity_id],
    staleTime: 1000 * 60 * 60 * 3, // 3 hours
    queryFn: async () => {
      const response = await api.v1.getEntityShareholderListV1SrShareholdersGet({
        entity_id: +entity_id,
      });
      return response.data;
    },
    enabled: !disabled,
  });
};

export const useTransactionAwaitingPaymentMutation = (registerId: number) => {
  return useMutation({
    mutationFn: async (payload: TransactionSetAwaitPaymentStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.transactionSetAwaitPaymentV1SrTransactionsStatusAwaitPaymentPatch(payload, {
          headers: { 'xRegister-ID': registerId },
        }),
        {
          loading: 'Updating transaction...',
          success: 'Transaction updated',
        },
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([ISSUER_CERTIFICATES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([INVESTOR_CERTIFICATES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_ISSUER_ACTIVITIES_QUERY_KEY]);
    },
  });
};

export const useTransactionSetRejectedMutation = (registerId: string) => {
  return useMutation({
    mutationFn: async (payload: TransactionSetRejectStatusRequest) => {
      const response = await withErrorHandling(
        api.v1.transactionSetRejectV1SrTransactionsStatusRejectedPatch(payload, {
          headers: { 'xRegister-ID': registerId },
        }),
        {
          loading: 'Updating transaction...',
          success: 'Transaction updated',
        },
      );

      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries([ISSUER_CERTIFICATES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([INVESTOR_CERTIFICATES_LIST_QUERY_KEY]);
      queryClient.invalidateQueries([CURRENT_ISSUER_ACTIVITIES_QUERY_KEY]);
    },
  });
};
